import { useEffect } from 'react';

import { useLocation } from 'react-router-dom';

import { chromeFor } from '@shared/app-identity/appChrome';
import { useInstalledApp } from '@shared/app-identity/useInstalledApp';

/** Maps route path prefixes to human-readable section names. */
const ROUTE_TITLES: [string, string][] = [
  // Order matters — first match wins, so the narrower mail prefix precedes the
  // surface root it sits inside.
  ['/attention/mail', 'Email'],
  ['/attention', 'Inbox'],
  ['/notes', 'Notes'],
  ['/journal', 'Journal'],
  // "Study", not "Study" — the surface was renamed and this label was not.
  ['/study', 'Study'],
  ['/food-diary', 'Food Diary'],
  ['/meals', 'Meals Planning'],
  ['/financial', 'Financial'],
  ['/my-data', 'My Data'],
  ['/chat', 'Chat'],
  ['/conversations', 'Conversations'],
  ['/knowledge', 'Knowledge'],
  ['/research', 'Research'],
  ['/quotes', 'Quotes'],
  // Profile and the admin tabs are items of the one Settings page now; their
  // old paths redirect here, so one prefix names them all.
  ['/settings', 'Settings'],
];

/**
 * Updates `document.title` to reflect the running app and active section —
 * "Conway - Email" in a browser tab, "Inbox - Email" inside Conway Inbox.
 *
 * The app name has to come from here as well as from the shell's static
 * `<title>`: this runs on mount and would otherwise overwrite the shell's
 * per-app title with "Conway" the moment React boots, undoing it.
 */
export function useDocumentTitle(): void {
  const { pathname } = useLocation();
  const appName = chromeFor(useInstalledApp()).shortName;

  useEffect(() => {
    const section = ROUTE_TITLES.find(([prefix]) => pathname.startsWith(prefix))?.[1];
    // A single-surface app is named after its one section, so qualifying it
    // with that same section says it twice — "Chat - Chat" in the dock.
    document.title = section && section !== appName ? `${appName} - ${section}` : appName;
  }, [pathname, appName]);
}
